// ONE form for any figure a person supplies.
//
// There used to be three: a usage-entry form for a metered line, a cost-base
// form for a recharge, and a usage-actual form on the deal page. All three
// wrote a different table through a different endpoint with different rules,
// and none of them could be fed by a machine.
//
// A fact is a fact whether a person typed it or a feed measured it. So this
// posts a ONE-ROW BATCH through the same audited ingest path a feed uses, with
// `source: "person"` — the row lands on `crm.deal_facts` under the same
// upsert-by-key rules, and a person's fact outranks every other source for the
// same key. When a feed for this kind of figure is written, nothing here
// changes: the same row arrives with a different `source`.
//
// A FACT HAS NO PATCH, and this form has no update mode. A correction is a
// re-ingest of the same key — the same line, the same period, the same kind —
// which is exactly what makes a person's write and a feed's write one
// operation. Sending the same figure twice is therefore harmless, and the
// batch reports how many rows it restated.
//
// FOUR KINDS, ONE SHAPE. An `amount` states money, a `quantity` states units, a
// `milestone_reached` states the DAY a deliverable landed, and an
// `entitlement_adjustment` moves a prepaid block's opening balance by a SIGNED
// quantity. The kind decides which field is asked for; everything else is the
// same four answers about which line, which period and who said so.
//
// AN ADJUSTMENT'S NOTE IS NOT OPTIONAL PROSE. It is shown beside the figure on
// the pool's position, so a person reading next month's statement learns why
// the balance moved. A signed quantity with no reason is a balance nobody can
// argue with.
//
// A MILESTONE ASKS FOR A DAY, NOT A MONTH. The month it bills in is the month
// of that day, so there is no second answer to get wrong. A fact that stated
// only the month made the runway date it to the month's last day, and a SOW
// signed on its planned day read "two weeks late".

import { useMemo, useState, type ReactNode } from "react";
import {
  DealFactKind,
  IngestDealFactsRequest,
  type Currency,
  type DealFactRow,
  type MeterUnit,
  type PrepaidEntitlement,
} from "@crm/shared";
import styles from "../../forms/forms.module.css";
import { useIngestDealFactsMutation } from "../../api/mutations.js";
import { useIdempotencyKey } from "../../api/idempotency.js";
import { Button } from "../../design/Button.js";
import { Dialog } from "../../design/Dialog.js";
import { Notice, ProblemMessage } from "../../design/Feedback.js";
import {
  DateField,
  MonthField,
  NumberField,
  ReadOnlyField,
  SelectField,
  TextField,
} from "../../forms/fields.js";
import { parseDecimal, parseInteger } from "../../forms/parseInput.js";
import { formatMonth, isCalendarDate, todayIsoDate } from "../../format/calendarDate.js";
import { formatQuantity, parseMoneyToMinor } from "../../format/money.js";
import { assertNever } from "../../lib/assertNever.js";

/**
 * What the row this dialog opens from already knows.
 *
 * Every field is optional except the period and the scope, because the two
 * entry points know different amounts: the statement panel opens on a line that
 * is short of a figure and can fill in all of it, while "Record a fact" on the
 * deal page knows only the deal.
 */
export interface FactPrefill {
  readonly dealId: string | null;
  readonly partnerId: string;
  readonly termLineId: string | null;
  readonly period: string;
  readonly kind: DealFactKind;
  /** The milestone the runway opened this on. Absent for every other kind. */
  readonly milestoneSeq?: number;
  /** The day a milestone was reached, for the date field. Today when absent. */
  readonly occurredOn?: string;
  /**
   * The pools this deal or partner holds, for an adjustment's picker. Empty
   * where the screen has none to offer, which is what hides the fourth kind:
   * an adjustment with no block to move is not a fact anybody can record.
   */
  readonly pools?: readonly PrepaidEntitlement[];
  readonly unit: MeterUnit | null;
  readonly currency: Currency;
  /** The line's own words, so the dialog can say what it is about. */
  readonly label: string | null;
}

interface FactDraft {
  kind: string;
  period: string;
  /** Money as typed. Parsed to minor units once, on submit. */
  amount: string;
  quantity: string;
  milestoneSeq: string;
  /** The day a milestone was reached. Its month is the period it bills in. */
  occurredOn: string;
  entitlementId: string;
  note: string;
}

function kindLabel(kind: DealFactKind): string {
  switch (kind) {
    case "amount":
      return "An amount of money";
    case "quantity":
      return "A count of units";
    case "milestone_reached":
      return "A milestone that landed";
    case "entitlement_adjustment":
      return "An adjustment to a prepaid block";
    default:
      return assertNever(kind, "deal fact kind");
  }
}

/**
 * A refusal, and the field it is about.
 *
 * The FIELD travels with the message. An adjustment asks three questions, and
 * a message that always lands under the last of them points a person at the
 * note they have already written when the quantity is what is missing. `field`
 * is null where a refusal is about the whole form — a body the contract
 * refuses names no one input this dialog draws.
 */
interface FactProblem {
  field: keyof FactDraft | null;
  message: string;
}

/** The one field this kind of fact is actually about, blank. */
function fieldProblem(draft: FactDraft, kind: DealFactKind): FactProblem | null {
  switch (kind) {
    case "amount":
      return draft.amount.trim() === ""
        ? { field: "amount", message: "Give the amount this period is worth." }
        : null;
    case "quantity":
      return draft.quantity.trim() === ""
        ? { field: "quantity", message: "Give the number of units." }
        : null;
    case "milestone_reached":
      if (draft.milestoneSeq.trim() === "") {
        return {
          field: "milestoneSeq",
          message: "Give the position of the milestone that landed.",
        };
      }
      return isCalendarDate(draft.occurredOn)
        ? null
        : { field: "occurredOn", message: "Give the day it was reached." };
    case "entitlement_adjustment":
      if (draft.entitlementId === "") {
        return { field: "entitlementId", message: "Say which prepaid block this moves." };
      }
      if (draft.quantity.trim() === "") {
        return { field: "quantity", message: "Give the signed quantity to move it by." };
      }
      // The note is what the position shows beside the figure next month. An
      // adjustment nobody explained is a balance nobody can argue with.
      return draft.note.trim() === ""
        ? { field: "note", message: "Say why the balance is moving." }
        : null;
    default:
      return assertNever(kind, "deal fact kind");
  }
}

export interface FactEntryDialogProps {
  prefill: FactPrefill;
  onClose: () => void;
}

export function FactEntryDialog({ prefill, onClose }: FactEntryDialogProps): ReactNode {
  const ingest = useIngestDealFactsMutation();
  const { keyFor } = useIdempotencyKey();
  const pools = prefill.pools ?? [];
  // An adjustment needs a block to move. Where a screen holds none, the kind is
  // not offered at all — an option that cannot be filled in is worse than one
  // that is absent.
  const kindOptions = DealFactKind.options
    .filter((kind) => kind !== "entitlement_adjustment" || pools.length > 0)
    .map((kind) => ({ value: kind, label: kindLabel(kind) }));
  const poolOptions = pools.map((pool) => ({
    value: pool.id,
    label: `${formatQuantity(pool.quantity)} ${pool.unit} · ${formatMonth(pool.period_start)} – ${formatMonth(pool.period_end)}`,
  }));
  const [draft, setDraft] = useState<FactDraft>({
    kind: prefill.kind,
    period: prefill.period,
    amount: "",
    quantity: "",
    milestoneSeq: prefill.milestoneSeq === undefined ? "" : String(prefill.milestoneSeq),
    occurredOn: prefill.occurredOn ?? todayIsoDate(),
    entitlementId: pools[0]?.id ?? "",
    note: "",
  });
  const [problem, setProblem] = useState<FactProblem | null>(null);

  /** The message this input carries, or none — one field, one message. */
  const messageFor = (field: keyof FactDraft): string | undefined =>
    problem?.field === field ? problem.message : undefined;

  const kind = useMemo<DealFactKind>(() => {
    const parsed = DealFactKind.safeParse(draft.kind);
    return parsed.success ? parsed.data : prefill.kind;
  }, [draft.kind, prefill.kind]);

  const setField = <K extends keyof FactDraft>(field: K, value: FactDraft[K]): void => {
    setDraft((previous) => ({ ...previous, [field]: value }));
    setProblem(null);
  };

  const submit = (): void => {
    const blank = fieldProblem(draft, kind);
    if (blank !== null) {
      setProblem(blank);
      return;
    }
    const row = rowOf(draft, kind, prefill);
    // A batch id per submission, minted from the body: a retry after a dropped
    // socket re-sends the same id and the server restates one row rather than
    // writing a second.
    const batchId = keyFor(row);
    const body = IngestDealFactsRequest.safeParse({
      batch_id: batchId,
      source: "person",
      rows: [row],
    });
    if (!body.success) {
      // The contract judged the whole row, so the message belongs to the form
      // rather than to one input this dialog happens to draw.
      setProblem({
        field: null,
        message: body.error.issues[0]?.message ?? "This fact does not match the contract.",
      });
      return;
    }
    ingest.mutate({ body: body.data, idempotencyKey: batchId }, { onSuccess: () => onClose() });
  };

  return (
    <Dialog
      open
      onClose={onClose}
      narrow
      title="Record a fact"
      onSubmit={submit}
      footer={
        <>
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={ingest.isPending}>
            {ingest.isPending ? "Recording…" : "Record the fact"}
          </Button>
        </>
      }
    >
      <p className={styles.description}>
        A fact is a fact whoever supplied it. This one is recorded as yours, and it beats any figure
        a feed later reports for the same line and period.
      </p>

      {ingest.error !== null && <ProblemMessage error={ingest.error} action="record this fact" />}

      {/* A refusal about the whole row rather than about one input. Without
          this it would be a form that will not submit and says nothing. */}
      {problem !== null && problem.field === null && (
        <Notice tone="warn" title="This fact was not recorded.">
          {problem.message}
        </Notice>
      )}

      {prefill.label !== null && (
        <ReadOnlyField
          label="About"
          value={prefill.label}
          reason="The line came from the row you opened, and a fact names its line."
        />
      )}

      <SelectField
        label="What kind of fact"
        value={draft.kind}
        options={kindOptions}
        onChange={(value) => setField("kind", value)}
        hint="An amount states money. A count states units, and the line prices them."
      />

      {kind === "milestone_reached" ? (
        <DateField
          label="Reached on"
          required
          value={draft.occurredOn}
          error={messageFor("occurredOn")}
          onChange={(value) => setField("occurredOn", value)}
          hint={
            isCalendarDate(draft.occurredOn)
              ? `The day it landed. It bills in ${formatMonth(draft.occurredOn.slice(0, 7))}, the month of that day.`
              : "The day it landed. It bills in the month of that day."
          }
        />
      ) : (
        <MonthField
          label="Period"
          required
          value={draft.period}
          onChange={(value) => setField("period", value)}
          hint={`The month this figure is FOR — ${formatMonth(prefill.period)} unless you say otherwise.`}
        />
      )}

      {kind === "amount" && (
        <NumberField
          label="Amount"
          required
          suffix={prefill.currency}
          value={draft.amount}
          error={messageFor("amount")}
          onChange={(value) => setField("amount", value)}
          hint="What this period is worth, in the deal's own currency."
        />
      )}

      {kind === "quantity" && (
        <NumberField
          label={`Quantity${prefill.unit === null ? "" : ` (${prefill.unit})`}`}
          required
          value={draft.quantity}
          error={messageFor("quantity")}
          onChange={(value) => setField("quantity", value)}
          hint="The count. The line's own price turns it into money, so nobody multiplies by hand."
        />
      )}

      {kind === "milestone_reached" && (
        <NumberField
          label="Milestone position"
          required
          value={draft.milestoneSeq}
          error={messageFor("milestoneSeq")}
          onChange={(value) => setField("milestoneSeq", value)}
          hint="The seq of the milestone in the line's plan."
        />
      )}

      {kind === "entitlement_adjustment" && (
        <>
          <SelectField
            label="Prepaid block"
            required
            value={draft.entitlementId}
            options={poolOptions}
            error={messageFor("entitlementId")}
            onChange={(value) => setField("entitlementId", value)}
            hint="Which block this moves. The adjustment lands on the period above and on no other."
          />
          <NumberField
            label="Adjustment"
            required
            suffix={poolUnit(pools, draft.entitlementId)}
            value={draft.quantity}
            error={messageFor("quantity")}
            onChange={(value) => setField("quantity", value)}
            hint="Signed, in the block's own unit: a credit adds, a correction takes away."
          />
          <TextField
            label="Why"
            required
            multiline
            value={draft.note}
            error={messageFor("note")}
            onChange={(value) => setField("note", value)}
            hint="Shown beside the figure on the block's position, so next month's reader knows why the balance moved."
          />
        </>
      )}
    </Dialog>
  );
}

/** The draft as the one row the batch carries. */
function rowOf(draft: FactDraft, kind: DealFactKind, prefill: FactPrefill): DealFactRow {
  const scope =
    prefill.dealId === null ? { partner_id: prefill.partnerId } : { deal_id: prefill.dealId };
  const base = {
    ...scope,
    ...(prefill.termLineId === null ? {} : { term_line_id: prefill.termLineId }),
    period: draft.period,
    kind,
  };
  switch (kind) {
    case "amount":
      return {
        ...base,
        amount_minor: parseMoneyToMinor(draft.amount, prefill.currency),
        currency: prefill.currency,
      };
    case "quantity":
      return { ...base, quantity: parseDecimal(draft.quantity) };
    case "milestone_reached":
      // The period IS the month of the day, so the two can never disagree.
      return {
        ...base,
        period: draft.occurredOn.slice(0, 7),
        milestone_seq: parseInteger(draft.milestoneSeq),
        occurred_on: draft.occurredOn,
      };
    case "entitlement_adjustment":
      return {
        ...base,
        entitlement_id: draft.entitlementId,
        quantity: parseDecimal(draft.quantity),
        note: draft.note.trim(),
      };
    default:
      return assertNever(kind, "deal fact kind");
  }
}

/** The unit of the block the picker is on, for the adjustment's suffix. */
function poolUnit(pools: readonly PrepaidEntitlement[], entitlementId: string): string {
  return pools.find((pool) => pool.id === entitlementId)?.unit ?? "units";
}
